import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  BOM_ITEM_STATUS,
  computeOrderSavings,
  formatUnitPrice,
  getActualOrderQty,
  getMatchingPriceSlab,
  getSlabUnitPrice,
  groupOtherMatches,
} from "utils/bomToolUtils";
import { useAuth } from "AuthContext";
import { updateBomItemAPI, deleteBomItemAPI } from "actions/bomItemActions";
import {
  Trash2,
  Check,
  ChevronDown,
  ChevronUp,
  Tag,
  Eye,
  EyeOff,
  RefreshCcw,
  ShoppingCart,
  Info,
} from "lucide-react";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { getBomItemsBatchRefreshAPI } from "actions/bomActions";
import BomOtherDistributors from "./BomOtherDistributors";
import BomPriceSlabModal from "./BomPriceSlabModal";
import DistributorErrorsTooltip from "./DistributorErrorsTooltip";

const MySwal = withReactContent(Swal);

const STATUS_MESSAGE = {
  [BOM_ITEM_STATUS.PENDING]: "Part number is being processed, please wait...",
  [BOM_ITEM_STATUS.PROCESSING]:
    "Part number is being processed, please wait...",
  [BOM_ITEM_STATUS.NO_MPN_FOUND]: "No MPN found",
  [BOM_ITEM_STATUS.STOCK_NOT_FOUND]: "Stock not found",
  [BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND]: "Multiple MPN found",
};

const STATUS_COLOR = {
  [BOM_ITEM_STATUS.PENDING]: "text-s360-info",
  [BOM_ITEM_STATUS.PROCESSING]: "text-s360-info",
  [BOM_ITEM_STATUS.NO_MPN_FOUND]: "text-s360-danger",
  [BOM_ITEM_STATUS.STOCK_NOT_FOUND]: "text-s360-warning",
  [BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND]: "text-s360-warning",
};

const ROW_BG = {
  [BOM_ITEM_STATUS.NO_MPN_FOUND]: "bg-[#FFF0F0]",
  [BOM_ITEM_STATUS.STOCK_NOT_FOUND]: "bg-[#FFF8F0]",
  [BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND]: "bg-[#ecebfe]",
  partial: "bg-[#DBEAFE]",
};

const ROW_HOVER = {
  [BOM_ITEM_STATUS.NO_MPN_FOUND]: "hover:bg-[#FFE0E0]",
  [BOM_ITEM_STATUS.STOCK_NOT_FOUND]: "hover:bg-[#FFF0E0]",
  [BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND]: "hover:bg-[#d6d5fa]",
  partial: "hover:bg-[#BFDBFE]",
};

const isPartialQty = (item) => {
  const opt = item?.optimal;
  if (!opt) return false;
  const avail = parseInt(String(opt.availability ?? 0).replace(/\D/g, ""), 10) || 0;
  const reqQty = parseInt(String(item?.actual_qty ?? item?.qty ?? 0), 10) || 0;
  return avail > 0 && reqQty > 0 && avail < reqQty;
};

const getRowBgClass = (itemStatus, item) => {
  if (itemStatus === BOM_ITEM_STATUS.NO_MPN_FOUND) return ROW_BG[BOM_ITEM_STATUS.NO_MPN_FOUND];
  if (itemStatus === BOM_ITEM_STATUS.STOCK_NOT_FOUND) return ROW_BG[BOM_ITEM_STATUS.STOCK_NOT_FOUND];
  if (isPartialQty(item)) return ROW_BG.partial;
  if (itemStatus === BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND) return ROW_BG[BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND];
  return "bg-white";
};

const getRowHoverClass = (itemStatus, item) => {
  if (itemStatus === BOM_ITEM_STATUS.NO_MPN_FOUND) return ROW_HOVER[BOM_ITEM_STATUS.NO_MPN_FOUND];
  if (itemStatus === BOM_ITEM_STATUS.STOCK_NOT_FOUND) return ROW_HOVER[BOM_ITEM_STATUS.STOCK_NOT_FOUND];
  if (isPartialQty(item)) return ROW_HOVER.partial;
  if (itemStatus === BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND) return ROW_HOVER[BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND];
  return "hover:bg-s360-stripe";
};

const Cell = ({ children, className = "" }) => (
  <td
    className={`px-3 py-2 text-sm text-s360-fg1 whitespace-nowrap ${className}`}
  >
    {children}
  </td>
);

const formatLastOrdered = (orderedAt) => {
  if (!orderedAt) return null;
  const d = new Date(orderedAt);
  if (isNaN(d.getTime())) return null;
  return d.toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
};

const BomItemsTable = ({
  bomItems = [],
  totalCount,
  onAddToCart,
  bomProcessingStatus,
  onLoad,
  onDistributorChange,
  lastOrderedMap = {},
  // Price column basis, owned by the page so downloads share it: false = Stock
  // Qty (each product's existing selectedPrice slab), true = RFQ Qty (looked up
  // fresh below). Falls back to local state if the page doesn't provide it.
  priceByRfqQty: priceByRfqQtyProp,
  setPriceByRfqQty: setPriceByRfqQtyProp,
}) => {
  const navigate = useNavigate();
  const { isInCart } = useAuth();
  const [selectedDist, setSelectedDist] = useState({});
  const [expandedRows, setExpandedRows] = useState({});
  const [slabModal, setSlabModal] = useState(null);
  const [selectedItems, setSelectedItems] = useState(new Set());
  const [priceByRfqQtyLocal, setPriceByRfqQtyLocal] = useState(false);
  const priceByRfqQty = priceByRfqQtyProp ?? priceByRfqQtyLocal;
  const setPriceByRfqQty = setPriceByRfqQtyProp ?? setPriceByRfqQtyLocal;

  const selectableItems = bomItems.filter((item) => item.optimal !== null);
  const allSelected =
    selectableItems.length > 0 &&
    selectableItems.every((item) => selectedItems.has(item.id));

  const toggleSelectItem = (id) => {
    setSelectedItems((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedItems(new Set());
    } else {
      setSelectedItems(new Set(selectableItems.map((item) => item.id)));
    }
  };

  const [editedPartNumbers, setEditedPartNumbers] = useState({});
  const [editedQtys, setEditedQtys] = useState({});
  const [refreshingItems, setRefreshingItems] = useState(new Set());
  const [refreshingDistributors, setRefreshingDistributors] = useState(new Set());
  const [all, setAll] = useState({ buttonLoading: false });

  const handlePartNumberChange = (id, value) => {
    setEditedPartNumbers((prev) => ({ ...prev, [id]: value }));
  };

  const handleQtyChange = (id, value) => {
    setEditedQtys((prev) => ({ ...prev, [id]: value }));
  };

  const handlePartNumberSubmit = (id) => {
    const value = editedPartNumbers[id];
    if (value !== undefined) handleChangePartNumber(id, value);
  };

  const handleQtySubmit = (id) => {
    const value = editedQtys[id];
    if (value !== undefined) handleChangeQty(id, value);
  };

  const handleChangePartNumber = async (id, value) => {
    if (bomProcessingStatus === "pending") {
      alert("Edits are allowed after the BOM processing is completed");
      return;
    }
    setAll({ ...all, buttonLoading: true });
    const payload = {
      id,
      part_number: value,
    };
    const res = await updateBomItemAPI(payload);
    console.log("Update Bom Item Response----->", { res });
    onLoad();
    setTimeout(() => {
      setAll({ ...all, buttonLoading: false });
    }, 1500);
  };

  const handleChangeQty = async (id, value) => {
    if (bomProcessingStatus === "pending") {
      alert("Edits are allowed after the BOM processing is completed");
      return;
    }

    const parsedQty = parseInt(value, 10);
    if (!parsedQty || parsedQty <= 0) {
      alert("Quantity must be greater than 0");
      return;
    }

    setAll({ ...all, buttonLoading: true });
    const res = await updateBomItemAPI({ id, actual_qty: String(parsedQty) });
    console.log("Update Actual Qty Response----->", { res });
    onLoad();
    setTimeout(() => {
      setAll({ ...all, buttonLoading: false });
    }, 1500);
  };

  const handleSaveActualQty = async (id, value) => {
    await updateBomItemAPI({ id, actual_qty: value });
  };

  const confirmAndAddToCart = async (cartItem) => {
    const requested = parseInt(cartItem.qty, 10) || 0;
    const availRaw = cartItem.optimal?.availability;
    const available =
      availRaw === null ||
      availRaw === undefined ||
      availRaw === "" ||
      availRaw === "None" ||
      availRaw === "N/A"
        ? 0
        : parseInt(availRaw, 10) || 0;

    if (available > 0 && available < requested) {
      const result = await MySwal.fire({
        title: "Stock is less than RFQ Qty",
        html: `Available stock is <strong>${available}</strong>, but you requested <strong>${requested}</strong>.<br/>What would you like to do?`,
        icon: "warning",
        showCancelButton: true,
        showDenyButton: true,
        focusConfirm: true,
        confirmButtonText: `Add only ${available}`,
        denyButtonText: `Add full ${requested}`,
        cancelButtonText: "Cancel",
        confirmButtonColor: "#1a3c5e",
        denyButtonColor: "#475569",
        cancelButtonColor: "#6b7280",
      });
      if (result.isDismissed) return;
      if (result.isConfirmed) {
        const product = cartItem.optimal;
        const slab =
          getMatchingPriceSlab(product, available) ??
          product?.price?.price_list?.[0] ??
          product?.selectedPrice;
        const unitPrice = getSlabUnitPrice(slab);
        onAddToCart?.({
          ...cartItem,
          qty: available,
          optimal: {
            ...product,
            selectedPrice: slab,
            actualOrderQty: available,
            totalPrice: unitPrice * available,
          },
        });
        return;
      }
      // result.isDenied → fall through and add full qty
    }
    onAddToCart?.(cartItem);
  };

  const handleRefreshDistributor = async (item, distributorName) => {
    const key = `${item.id}-${distributorName}`;
    setRefreshingDistributors((prev) => new Set(prev).add(key));
    try {
      const response = await getBomItemsBatchRefreshAPI({ bomIds: [item.id], distributor: distributorName });
      if (response.status) {
        onLoad();
      }
    } catch (e) {
      // ignore
    } finally {
      setRefreshingDistributors((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  };

  const handleRefreshItem = async (item) => {
    setRefreshingItems((prev) => new Set(prev).add(item.id));
    try {
      const response = await getBomItemsBatchRefreshAPI({ bomIds: [item.id] });
      // setTimeout(() => {
        if(response.status) {
          onLoad();
        }
        setRefreshingItems((prev) => {
          const next = new Set(prev);
          next.delete(item.id);
          return next;
        });
      // }, 3000);
    } catch (e) {
      setRefreshingItems((prev) => {
        const next = new Set(prev);
        next.delete(item.id);
        return next;
      });
    }
  };

  const handleDelete = async (id, partNumber) => {
    if (bomProcessingStatus === "pending") {
      alert("Deletion is allowed after the BOM processing is completed");
      return;
    }

    const result = await MySwal.fire({
      title: "Delete BOM Item?",
      html: `Are you sure you want to delete <strong>${partNumber}</strong>?<br/>This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      cancelButtonColor: "#6b7280",
      confirmButtonText: "Yes, delete it",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    setAll({ ...all, buttonLoading: true });
    const res = await deleteBomItemAPI({ id });
    console.log("Delete Bom Item Response----->", { res });

    if (res.status) {
      MySwal.fire({
        title: "Deleted!",
        text: `${partNumber} has been deleted successfully.`,
        icon: "success",
        timer: 2000,
        showConfirmButton: false,
      });
      onLoad();
    } else {
      MySwal.fire({
        title: "Error!",
        text: res.message || "Failed to delete BOM item.",
        icon: "error",
      });
    }

    setTimeout(() => {
      setAll({ ...all, buttonLoading: false });
    }, 1500);
  };

  const toggleExpandedRow = (id) => {
    setExpandedRows((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Add every distributor in a cross-distributor split to the cart. Qty per
  // segment comes from the RFQ Qty the user has in the split table (falling
  // back to the segment's suggested qty if none was edited) rather than the
  // suggested qty itself. Distinct distributors are separate cart lines, so
  // this fires one add per segment.
  const handleAddSplitToCart = (item, split, rfqQtys) => {
    const segments = split?.segments ?? [];
    if (segments.length === 0) return;
    let totalQty = 0;
    segments.forEach((seg, segIdx) => {
      const qty = rfqQtys?.[segIdx] ?? seg.qty;
      totalQty += qty;
      onAddToCart?.(
        {
          ...item,
          qty,
          optimal: {
            ...seg.product,
            selectedPrice: seg.slab,
            actualOrderQty: qty,
            totalPrice: seg.unitPrice * qty,
          },
        },
        true,
      );
    });

    MySwal.fire({
      title: <strong>Cart Update!</strong>,
      html: (
        <i>
          Added {segments.length} distributor line
          {segments.length !== 1 ? "s" : ""} for{" "}
          {item.part_number} (total qty{" "}
          {totalQty.toLocaleString()}) to cart
        </i>
      ),
      icon: "success",
      showCancelButton: true,
      confirmButtonText: "View Cart",
      cancelButtonText: "Continue Shopping",
      confirmButtonColor: "#5F47C9",
      cancelButtonColor: "#6b7280",
    }).then((result) => {
      if (result.isConfirmed) navigate("/cart");
    });
  };

  const handleAddAllToCart = () => {
    const added = [];
    bomItems.forEach((item) => {
      if (!item.optimal) return;
      const activeKey =
        selectedDist[item.id] ?? item.savedDistributorKey ?? item.optimalKey;
      const activeProduct =
        (item.distributorOptions || []).find((o) => o.key === activeKey)?.product ?? item.optimal;
      const qty = item.actual_qty ?? item.qty;
      onAddToCart?.({ ...item, qty, optimal: activeProduct }, true);
      added.push({ partNumber: activeProduct?.part_number ?? item.part_number, qty });
    });
    if (added.length === 0) return;
    const count = added.length;
    const preview = added
      .slice(0, 3)
      .map((i) => `${i.partNumber} (Qty: ${parseInt(i.qty).toLocaleString()})`)
      .join(", ");
    const more = count > 3 ? ` and ${count - 3} more` : "";
    MySwal.fire({
      title: <strong>Cart Update!</strong>,
      html: (
        <i>
          {count === 1
            ? `You've added ${preview} to cart`
            : `You've added ${count} items to cart: ${preview}${more}`}
        </i>
      ),
      icon: "success",
      showCancelButton: true,
      confirmButtonText: "View Cart",
      cancelButtonText: "Continue Shopping",
      confirmButtonColor: "#5F47C9",
      cancelButtonColor: "#6b7280",
    }).then((result) => {
      if (result.isConfirmed) navigate("/cart");
    });
  };

  if (!bomItems.length) return null;

  return (
    <>
      <div className="bg-white rounded-md shadow-sm border border-s360-stroke1 overflow-hidden">
        {/* Header */}
        <div className="flex items-center gap-3 px-[18px] py-3.5 border-b border-s360-stroke1 flex-wrap">
          <h3 className="text-base font-semibold text-s360-fg1">BOM items</h3>
          <span className="text-xs text-s360-fg3">
            {bomItems.length}
            {totalCount ? ` of ${totalCount}` : ""} shown
          </span>
          <div className="ml-auto flex items-center gap-2">
            {selectedItems.size > 0 && (
              <span className="text-[13px] text-s360-violet font-medium">
                {selectedItems.size} selected
              </span>
            )}
            <button
              onClick={toggleSelectAll}
              disabled={selectableItems.length === 0}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2 disabled:opacity-50"
            >
              <span
                className={`w-4 h-4 rounded border inline-flex items-center justify-center ${
                  allSelected ? "bg-s360-violet border-s360-violet text-white" : "border-s360-stroke1"
                }`}
              >
                {allSelected && <Check className="w-3 h-3" />}
              </span>
              {allSelected ? "Deselect all" : "Select all"}
            </button>
            <button
              onClick={handleAddAllToCart}
              disabled={selectableItems.length === 0}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-s360-violet text-white text-[13px] font-semibold hover:bg-s360-violet-600 disabled:opacity-50"
            >
              <ShoppingCart className="w-3.5 h-3.5" /> Add all to cart
            </button>
            <button
              onClick={onLoad}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2"
            >
              <RefreshCcw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              type="button"
              onClick={() => setPriceByRfqQty((v) => !v)}
              className="inline-flex items-center gap-2 h-8 px-3 rounded-md bg-white border border-s360-stroke1 text-[13px] font-semibold text-s360-fg1 hover:bg-s360-bg2"
              title="Toggle the Price column between Stock Qty and RFQ Qty pricing"
            >
              <span
                className={`relative inline-flex h-4 w-8 shrink-0 items-center rounded-full transition-colors ${
                  priceByRfqQty ? "bg-s360-violet" : "bg-s360-stroke1"
                }`}
              >
                <span
                  className={`inline-block h-3 w-3 transform rounded-full bg-white transition-transform ${
                    priceByRfqQty ? "translate-x-4" : "translate-x-0.5"
                  }`}
                />
              </span>
              Price: {priceByRfqQty ? "RFQ Qty" : "Stock Qty"}
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-s360-bg2 text-sm">
          <thead className="bg-s360-bg2">
            <tr>
              <th className="px-3 py-3">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                  className="w-4 h-4 rounded border-s360-stroke1 accent-s360-violet cursor-pointer"
                  title="Select all found items"
                />
              </th>
              {[
                "#",
                "SKU",
                "Manufacturer",
                "Distributor",
                "HS Code",
                "Date Code",
                "RFQ QTY",
                "Stock",
                "MOQ",
                "MUL",
                "Price",
                "",
              ].map((col) => (
                <th
                  key={col}
                  className="px-3 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.08em] text-s360-fg3 border-b border-s360-stroke1 whitespace-nowrap"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-s360-bg2">
            {bomItems.map((item, index) => {
              const {
                optimal,
                distributorOptions = [],
                otherMatches = [],
                distributorErrors = [],
                distributorEmptyResults = [],
                itemStatus,
                part_number,
                qty,
                optimalKey,
                savedDistributorKey,
              } = item;
              const isFound = itemStatus === BOM_ITEM_STATUS.FOUND;
              const isExpanded = !!expandedRows[item.id];

              // Cross-distributor split: present only when no single distributor
              // can cover the RFQ qty and at least two are needed to fulfill it.
              const crossSplit = item.crossDistributorSplit;
              const hasCrossSplit = (crossSplit?.segments?.length ?? 0) >= 2;

              // Only honor a saved selection that still maps to an available option.
              // Older saved values used a "distributor||packageType" key; fall back
              // to matching just the distributor portion for those.
              const legacyDist = savedDistributorKey?.split("||")[0];
              const savedKey =
                distributorOptions.find(
                  (o) =>
                    o.key === savedDistributorKey || o.key === legacyDist,
                )?.key ?? null;
              const activeKey = selectedDist[item.id] ?? savedKey ?? optimalKey;
              const activeOption = distributorOptions.find(
                (o) => o.key === activeKey,
              );
              const activeProduct = activeOption?.product ?? optimal;

              // Price column basis: default (Stock Qty) keeps the existing
              // selectedPrice slab as-is. Split orders keep their weighted-
              // average selectedPrice either way, since that blended price
              // can't be represented by a single price_list slab lookup.
              const rfqQtyForPricing = parseInt(item.actual_qty ?? qty, 10) || 0;
              const displaySlab =
                priceByRfqQty && activeProduct && !activeProduct.isSplitOrder
                  ? (getMatchingPriceSlab(
                      activeProduct,
                      getActualOrderQty(activeProduct, rfqQtyForPricing),
                    ) ?? activeProduct.selectedPrice)
                  : activeProduct?.selectedPrice;

              // The currently-active distributor is already shown in the main
              // row, so drop its leftover packaging variants (e.g. Digikey's
              // Cut Tape/Digi-Reel that lost to a cheaper TR+CT split) from
              // "other matches" instead of listing that distributor twice.
              // Switching distributors re-includes the previously-active one.
              const otherMatchesForActive = activeProduct
                ? otherMatches.filter((m) => m.distributor !== activeProduct.distributor)
                : otherMatches;
              // The expanded "other matches" panel collapses packaging
              // variants of the same distributor into one row, so the count
              // badge here must reflect that grouped total, not the raw list.
              const otherMatchesCount = groupOtherMatches(otherMatchesForActive).length;

              const handleDistChange = (e) => {
                const newKey = e.target.value;
                setSelectedDist((prev) => ({
                  ...prev,
                  [item.id]: newKey,
                }));
                const newProduct =
                  distributorOptions.find((o) => o.key === newKey)
                    ?.product ?? optimal;
                onDistributorChange?.(item.id, newProduct);
                // Persist the choice so it is restored when the BOM is reopened
                updateBomItemAPI({ id: item.id, selected_distributor: newKey });
              };

              const isSelectable = item.optimal !== null;

              const isEditingPartNumber =
                editedPartNumbers[item.id] !== undefined &&
                editedPartNumbers[item.id] !== part_number;
              const lastOrdered = !isEditingPartNumber
                ? lastOrderedMap?.[part_number]
                : null;
              const lastOrderedText = formatLastOrdered(lastOrdered?.ordered_at);

              return (
                <React.Fragment key={item.id}>
                  <tr className={`${getRowBgClass(item.itemStatus, item)} ${getRowHoverClass(item.itemStatus, item)} transition-colors`}>
                    <td className="px-3 py-2">
                      <input
                        type="checkbox"
                        checked={selectedItems.has(item.id)}
                        onChange={() => toggleSelectItem(item.id)}
                        disabled={!isSelectable}
                        className="w-4 h-4 rounded border-s360-stroke1 accent-s360-violet cursor-pointer disabled:opacity-30 disabled:cursor-not-allowed"
                      />
                    </td>
                    <Cell className="text-s360-fg3">{index + 1}</Cell>
                    <Cell>
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={editedPartNumbers[item.id] ?? part_number}
                          onChange={(e) =>
                            handlePartNumberChange(item.id, e.target.value)
                          }
                          className="width-180 text-sm font-medium border border-s360-stroke1 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-s360-violet/20 focus:border-s360-violet bg-white"
                        />
                        {editedPartNumbers[item.id] !== undefined &&
                          editedPartNumbers[item.id] !== part_number && (
                            <button
                              onClick={() => handlePartNumberSubmit(item.id)}
                              disabled={all.buttonLoading}
                              className="bg-s360-violet hover:bg-s360-violet-600 disabled:opacity-60 disabled:cursor-not-allowed text-white p-1.5 rounded transition-colors flex items-center justify-center"
                              title="Update part number"
                            >
                              {all.buttonLoading ? (
                                <svg
                                  className="animate-spin h-4 w-4 text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  />
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8v8z"
                                  />
                                </svg>
                              ) : (
                                <Check className="w-4 h-4" />
                              )}
                            </button>
                          )}
                      </div>
                      {lastOrderedText && (
                        <div
                          className="mt-1 text-xs text-amber-700 flex items-center gap-1"
                          title={
                            lastOrdered?.distributor
                              ? `Last ordered via ${lastOrdered.distributor}` +
                                (lastOrdered.qty
                                  ? ` — qty ${lastOrdered.qty}`
                                  : "")
                              : undefined
                          }
                        >
                          <ShoppingCart className="w-3 h-3 shrink-0" />
                          <span>Recently ordered on {lastOrderedText}</span>
                        </div>
                      )}
                      {isFound && activeProduct && hasCrossSplit && (
                        <button
                          onClick={() => toggleExpandedRow(item.id)}
                          className="mt-1 flex items-start gap-1 text-left text-[11px] text-s360-violet hover:text-s360-violet-600 font-medium"
                          title="View distributor split breakdown"
                        >
                          <Info className="w-3 h-3 shrink-0 mt-0.5" />
                          <span>
                            No single distributor can fulfill{" "}
                            {(crossSplit.requiredQty ?? 0).toLocaleString()} - order
                            will be split across {crossSplit.segments.length}{" "}
                            distributors
                          </span>
                        </button>
                      )}
                    </Cell>

                    {isFound && activeProduct ? (
                      <>
                        <Cell>{activeProduct.manufacturer ?? "-"}</Cell>
                        <Cell>
                          {distributorOptions.length > 1 ? (
                            <select
                              value={activeKey}
                              onChange={handleDistChange}
                              className="w-28 text-[13px] font-semibold text-s360-violet bg-s360-violet-50 border border-s360-violet-100 rounded-md px-2 py-1 cursor-pointer focus:outline-none focus:ring-2 focus:ring-s360-violet/20"
                            >
                              {distributorOptions.map((o) => (
                                <option key={o.key} value={o.key} className="text-s360-fg1 bg-white">
                                  {o.label}
                                </option>
                              ))}
                            </select>
                          ) : (
                            <span className="w-28 inline-flex items-center text-[13px] font-semibold text-s360-violet bg-s360-violet-50 border border-s360-violet-100 rounded-md px-2 py-1">
                              {distributorOptions[0]?.label ?? activeProduct.distributor ?? "-"}
                            </span>
                          )}
                        </Cell>
                        <Cell>{activeProduct.hts_code ?? "-"}</Cell>
                        <Cell>{activeProduct.date_code || "-"}</Cell>
                        <Cell>
                          <div className="flex items-center gap-1">
                            <input
                              type="number"
                              min="1"
                              value={editedQtys[item.id] ?? item.actual_qty ?? qty}
                              onChange={(e) =>
                                handleQtyChange(item.id, e.target.value)
                              }
                              className="w-20 text-sm font-medium border border-s360-stroke1 rounded px-2 py-1 focus:outline-none focus:ring-1 focus:ring-s360-violet/20 focus:border-s360-violet bg-white"
                            />
                            {editedQtys[item.id] !== undefined &&
                              String(editedQtys[item.id]) !==
                                String(item.actual_qty ?? qty) && (
                                <button
                                  onClick={() => handleQtySubmit(item.id)}
                                  disabled={all.buttonLoading}
                                  className="bg-s360-violet hover:bg-s360-violet-600 disabled:opacity-60 disabled:cursor-not-allowed text-white p-1.5 rounded transition-colors flex items-center justify-center"
                                  title="Update quantity"
                                >
                                  {all.buttonLoading ? (
                                    <svg
                                      className="animate-spin h-4 w-4 text-white"
                                      xmlns="http://www.w3.org/2000/svg"
                                      fill="none"
                                      viewBox="0 0 24 24"
                                    >
                                      <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                      />
                                      <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8v8z"
                                      />
                                    </svg>
                                  ) : (
                                    <Check className="w-4 h-4" />
                                  )}
                                </button>
                              )}
                          </div>
                          {qty != null &&
                            String(editedQtys[item.id] ?? item.actual_qty ?? qty) !==
                              String(qty) && (
                              <div className="mt-1 text-[11px] text-s360-fg3 whitespace-nowrap">
                                Original qty: {qty}
                              </div>
                            )}
                        </Cell>
                        <Cell>
                          {(() => {
                            const av =
                              activeProduct.displayAvailability ??
                              activeProduct.availability;
                            const isNoStock =
                              av === null ||
                              av === undefined ||
                              av === "" ||
                              av === "0" ||
                              av === 0 ||
                              av === "None" ||
                              av === "N/A";
                            // Factory stock (DigiKey's ManufacturerPublicQuantity) —
                            // manufacturer-held stock, shown alongside the in-stock
                            // number. Purely informational; back-order/split math
                            // stays on `availability`.
                            const factoryStock =
                              parseInt(
                                String(activeProduct.factory_stock ?? "").replace(/\D/g, ""),
                                10,
                              ) || 0;
                            return (
                              <div>
                                {isNoStock ? (
                                  <span className="text-s360-danger font-medium">
                                    No Stock
                                  </span>
                                ) : (
                                  <span className="text-s360-success font-medium">
                                    {av}
                                  </span>
                                )}
                                {factoryStock > 0 && (
                                  <div className="text-[11px] text-s360-fg3 mt-0.5 whitespace-nowrap">
                                    Factory: {factoryStock.toLocaleString()}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </Cell>
                        
                        <Cell>
                          {displaySlab?.min_qty ?? "-"}
                          {activeProduct.isSplitOrder && activeProduct.splitFirst && activeProduct.splitSecond && (
                            <div className="text-xs text-s360-violet mt-0.5">
                              {activeProduct.splitFirst.qty.toLocaleString()} TR + {activeProduct.splitSecond.qty.toLocaleString()} CT
                            </div>
                          )}
                        </Cell>
                        <Cell>{activeProduct.isSplitOrder ? "-" : (activeProduct.price?.multiple ?? "-")}</Cell>
                        <Cell className="font-semibold">
                          {formatUnitPrice(displaySlab) != null
                            ? `${activeProduct.price?.currency ?? displaySlab?.currency ?? ""} ${formatUnitPrice(displaySlab)}`.trim()
                            : "-"}
                          {activeProduct.isSplitOrder && (
                            <div className="text-xs text-s360-violet mt-0.5 font-normal">weighted avg</div>
                          )}
                          {(() => {
                            // When the requested qty can't be met from stock, the
                            // slab above is priced for the available stock, not the
                            // requested qty — flag that so the price isn't misread.
                            // Suppressed when a cross-distributor split note already
                            // explains the shortfall below the distributor, or when
                            // the Price column is already showing RFQ-qty pricing.
                            if (activeProduct.isSplitOrder || hasCrossSplit || priceByRfqQty) return null;
                            const avail = parseInt(
                              String(
                                activeProduct.displayAvailability ??
                                  activeProduct.availability ??
                                  "",
                              ).replace(/\D/g, ""),
                              10,
                            );
                            const orderQty =
                              activeProduct.actualOrderQty ??
                              parseInt(item.actual_qty ?? qty, 10);
                            if (!avail || avail <= 0 || avail >= orderQty)
                              return null;
                            return (
                              <div className="text-[11px] text-s360-warning mt-0.5 font-normal whitespace-nowrap">
                                Priced for available stock: {avail.toLocaleString()}
                              </div>
                            );
                          })()}
                        </Cell>
                        <Cell>
                          <div className="flex items-center justify-end gap-2">
                            <DistributorErrorsTooltip
                              distributorErrors={distributorErrors}
                            />
                            {(otherMatchesForActive.length > 0 ||
                              distributorErrors.length > 0 ||
                              distributorEmptyResults.length > 0 ||
                              hasCrossSplit) && (
                              <button
                                onClick={() => toggleExpandedRow(item.id)}
                                className="inline-flex items-center gap-1 border border-s360-stroke1 bg-white hover:bg-s360-bg2 text-s360-fg1 px-2 py-1 rounded text-xs font-medium transition-colors"
                                title={hasCrossSplit ? "View distributor split & other matches" : undefined}
                              >
                                {isExpanded ? (
                                  <EyeOff className="w-3.5 h-3.5" />
                                ) : (
                                  <Eye className="w-3.5 h-3.5" />
                                )}
                                {isExpanded
                                  ? ""
                                  : otherMatchesCount > 0
                                    ? ` (${otherMatchesCount})`
                                    : ""}
                              </button>
                            )}
                            {(activeProduct.isSplitOrder || activeProduct.price?.price_list?.length > 0) && (
                              <button
                                onClick={() => {
                                  const stockRaw = activeProduct.displayAvailability ?? activeProduct.availability;
                                  const stockNum = parseInt(String(stockRaw ?? 0).replace(/\D/g, ""), 10) || 0;
                                  const rfqNum = parseInt(item.actual_qty ?? qty, 10) || 0;
                                  const isPricedForStock = !priceByRfqQty && stockNum > 0 && stockNum < rfqNum;
                                  setSlabModal({
                                    product: { ...activeProduct, selectedPrice: displaySlab },
                                    partNumber: part_number,
                                    packageOptions: activeOption?.packageOptions,
                                    pricingBasis: isPricedForStock ? "stock" : "rfq",
                                    stockQty: isPricedForStock ? stockNum : null,
                                  });
                                }}
                                className="inline-flex items-center gap-1 border border-s360-violet-100 bg-s360-violet-50 hover:bg-s360-violet-100 text-s360-violet px-2 py-1 rounded text-xs font-medium transition-colors"
                                title="View price slabs"
                              >
                                <Tag className="w-3.5 h-3.5" />
                              </button>
                            )}
                            {isInCart(activeProduct) ? (
                              <button
                                onClick={() => navigate("/cart")}
                                className="inline-flex items-center gap-1 bg-s360-success hover:bg-[#0B5A0B] text-white p-1.5 rounded transition-colors"
                                title="Go to cart"
                              >
                                <ShoppingCart className="w-4 h-4" />
                              </button>
                            ) : (
                              <button
                                onClick={() =>
                                  confirmAndAddToCart({
                                    ...item,
                                    qty: item.actual_qty ?? item.qty,
                                    optimal: activeProduct,
                                  })
                                }
                                className="inline-flex items-center gap-1 bg-s360-violet hover:bg-s360-violet-600 text-white p-1.5 rounded transition-colors"
                                title="Add to cart"
                              >
                                <ShoppingCart className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(item.id, part_number)}
                              className="bg-s360-danger hover:bg-[#A80D1A] text-white p-1.5 rounded transition-colors"
                              title="Delete BOM item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </Cell>
                      </>
                    ) : (
                      <>
                        <td
                          colSpan={9}
                          className={`px-3 py-2 text-sm font-medium ${
                            isFound
                              ? "text-s360-warning"
                              : (STATUS_COLOR[itemStatus] ?? "text-s360-fg3")
                          }`}
                        >
                          {isFound
                            ? "Not available from the selected distributors"
                            : (STATUS_MESSAGE[itemStatus] ?? itemStatus)}
                        </td>
                        <Cell>
                          <div className="flex items-center justify-end gap-2">
                            <DistributorErrorsTooltip
                              distributorErrors={distributorErrors}
                            />
                            {(itemStatus === BOM_ITEM_STATUS.NO_MPN_FOUND ||
                              itemStatus === BOM_ITEM_STATUS.STOCK_NOT_FOUND) && (
                              <button
                                onClick={() => handleRefreshItem(item)}
                                disabled={refreshingItems.has(item.id)}
                                className="inline-flex items-center gap-1 border border-s360-violet-100 bg-s360-violet-50 hover:bg-s360-violet-100 text-s360-violet px-2 py-1 rounded text-xs font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                title="Refresh BOM item"
                              >
                                <RefreshCcw className={`w-3.5 h-3.5 ${refreshingItems.has(item.id) ? "animate-spin" : ""}`} />
                              </button>
                            )}
                            {isFound && otherMatchesForActive.length > 0 && (
                              <button
                                onClick={() => toggleExpandedRow(item.id)}
                                className="inline-flex items-center gap-1 border border-s360-stroke1 bg-white hover:bg-s360-bg2 text-s360-fg1 px-2 py-1 rounded text-xs font-medium transition-colors"
                                title="View available options"
                              >
                                {isExpanded ? (
                                  <EyeOff className="w-3.5 h-3.5" />
                                ) : (
                                  <Eye className="w-3.5 h-3.5" />
                                )}
                                {!isExpanded && ` (${otherMatchesCount})`}
                              </button>
                            )}
                            {itemStatus === BOM_ITEM_STATUS.MULTIPLE_MPN_FOUND && otherMatchesForActive.length > 0 && (
                              <button
                                onClick={() => toggleExpandedRow(item.id)}
                                className="inline-flex items-center gap-1 border border-s360-stroke1 bg-white hover:bg-s360-bg2 text-s360-fg1 px-2 py-1 rounded text-xs font-medium transition-colors"
                                title="View matched products"
                              >
                                {isExpanded ? (
                                  <EyeOff className="w-3.5 h-3.5" />
                                ) : (
                                  <Eye className="w-3.5 h-3.5" />
                                )}
                                {!isExpanded && ` (${otherMatchesCount})`}
                              </button>
                            )}
                            <button
                              onClick={() => handleDelete(item.id, part_number)}
                              className="bg-s360-danger hover:bg-[#A80D1A] text-white p-1.5 rounded transition-colors"
                              title="Delete BOM item"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </Cell>
                      </>
                    )}
                  </tr>
                  <BomOtherDistributors
                    rowId={item.id}
                    isFound={isFound}
                    activeProduct={activeProduct}
                    isExpanded={isExpanded}
                    otherMatches={otherMatchesForActive}
                    distributorErrors={distributorErrors}
                    distributorEmptyResults={distributorEmptyResults}
                    index={index}
                    onRefreshDistributor={(distributorName) => handleRefreshDistributor(item, distributorName)}
                    refreshingDistributors={refreshingDistributors}
                    itemId={item.id}
                    onShowSlabs={(product, packageOptions) =>
                      setSlabModal({ product, partNumber: part_number, packageOptions })
                    }
                    actualQty={item.actual_qty}
                    onSaveActualQty={(value) => handleSaveActualQty(item.id, value)}
                    onAddToCart={(matchProduct) =>
                      confirmAndAddToCart({
                        ...item,
                        optimal: matchProduct,
                        qty: matchProduct.qty ?? item.qty,
                      })
                    }
                    crossSplit={isFound && activeProduct && hasCrossSplit ? crossSplit : null}
                    onAddSplitToCart={(rfqQtys) => handleAddSplitToCart(item, crossSplit, rfqQtys)}
                  />
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>

      <BomPriceSlabModal
        product={slabModal?.product ?? null}
        packageOptions={slabModal?.packageOptions}
        onClose={() => setSlabModal(null)}
        pricingBasis={slabModal?.pricingBasis}
        stockQty={slabModal?.stockQty}
      />
    </>
  );
};

export default BomItemsTable;
